/**
 * Types of Triggers based on triggering event types
 *  with helpers/templates for configuration strings
 */
import React from 'react';

import {TriggerScopeType} from '../../../generated/graphql';
import {OptionType} from './CreateTriggerTab';
import {
  ArtifactAliasFilterCreator,
  ArtifactCollectionFilterCreator,
  EventConfigurationProps,
  LinkModelFilterCreator,
} from './TriggerEventConfiguration';

export type Trigger = {
  text: string;
  defaultEventScopeType: TriggerScopeType;
  templateStrings: TemplateValue[];
  eventConfiguration?: React.FC<EventConfigurationProps>;
};

type TemplateValue = {
  value: string;
  help: string;
};

const artifactTriggerTemplateValues: TemplateValue[] = [
  {
    value: '${artifact_version}', // eslint-disable-line no-template-curly-in-string
    help: 'Refers to the specific artifact version that triggered the action. Passed as an artifact instance.',
  },
  {
    value: '${artifact_version_string}', // eslint-disable-line no-template-curly-in-string
    help: 'Refers to the specific artifact version that triggered the action. Passed as a string.',
  },
  {
    value: '${artifact_collection_name}', // eslint-disable-line no-template-curly-in-string
    help: 'Refers to the name of the artifact collection that the artifact version is linked to.',
  },
];

const aliasTriggerTemplateValues: TemplateValue[] = [
  {
    value: '${alias}', // eslint-disable-line no-template-curly-in-string
    help: 'Refers to the alias that is being updated.',
  },
];

const projectTriggerTemplateValues: TemplateValue[] = [
  {
    value: '${project_name}', // eslint-disable-line no-template-curly-in-string
    help: 'Refers to the name of the project owning the mutation that triggered the action.',
  },
];

const entityTriggerTemplateValues: TemplateValue[] = [
  {
    value: '${entity_name}', // eslint-disable-line no-template-curly-in-string
    help: 'Refers to the name of the entity owning the mutation that triggered the action.',
  },
];

export const AddAliasTrigger: Trigger = {
  text: 'An artifact alias is added',
  defaultEventScopeType: TriggerScopeType.Project,
  templateStrings: [
    ...aliasTriggerTemplateValues,
    ...artifactTriggerTemplateValues,
    ...projectTriggerTemplateValues,
    ...entityTriggerTemplateValues,
  ],
  eventConfiguration: ArtifactAliasFilterCreator,
};

export const CreateArtifactTrigger: Trigger = {
  text: 'A new version of an artifact is created in a collection',
  defaultEventScopeType: TriggerScopeType.ArtifactCollection,
  templateStrings: [
    ...artifactTriggerTemplateValues,
    ...projectTriggerTemplateValues,
    ...entityTriggerTemplateValues,
  ],
  eventConfiguration: ArtifactCollectionFilterCreator,
};

export const UpdateAliasTrigger: Trigger = {
  text: 'An artifact alias is updated',
  defaultEventScopeType: TriggerScopeType.Project,
  templateStrings: [
    ...aliasTriggerTemplateValues,
    ...artifactTriggerTemplateValues,
    ...projectTriggerTemplateValues,
    ...entityTriggerTemplateValues,
  ],
  eventConfiguration: ArtifactAliasFilterCreator,
};

export const LinkModelTrigger: Trigger = {
  text: 'A new version is added to a registered model',
  defaultEventScopeType: TriggerScopeType.ArtifactCollection,
  templateStrings: [
    ...artifactTriggerTemplateValues,
    ...projectTriggerTemplateValues,
    ...entityTriggerTemplateValues,
  ],
  eventConfiguration: LinkModelFilterCreator,
};

export enum EventTriggeringConditionType {
  CreateArtifact = 'CREATE_ARTIFACT',
  AddArtifactAlias = 'ADD_ARTIFACT_ALIAS',
  UpdateArtifactAlias = 'UPDATE_ARTIFACT_ALIAS',
  LinkModel = 'LINK_MODEL',
}

export const triggers: {[key in EventTriggeringConditionType]: Trigger} = {
  [EventTriggeringConditionType.AddArtifactAlias]: AddAliasTrigger,
  [EventTriggeringConditionType.CreateArtifact]: CreateArtifactTrigger,
  [EventTriggeringConditionType.LinkModel]: LinkModelTrigger,
  [EventTriggeringConditionType.UpdateArtifactAlias]: UpdateAliasTrigger,
};

export const triggerOptions: OptionType[] = Object.keys(triggers).map(
  (key: string) => {
    return {
      label: triggers[key as EventTriggeringConditionType].text,
      value: key,
    };
  }
);
